import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../App';
import { Button } from '../ui/button';
import LanguageSwitcher from './LanguageSwitcher';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { 
  Menu, X, Search, User, LogOut, LayoutDashboard, 
  FileText, Truck, Landmark, ShoppingCart, Calculator,
  ChevronDown, Shield
} from 'lucide-react';

const ATFF_LOGO = "https://customer-assets.emergentagent.com/job_africa-trade-hub-5/artifacts/0f6s53y1_Untitled%20design%20%285%29.png";

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navLinks = [
    { href: '/marketplace', label: t('nav.marketplace'), icon: ShoppingCart },
    { href: '/compliance-engine', label: t('nav.tradeCompliance'), icon: Shield },
    { href: '/logistics', label: t('nav.logistics'), icon: Truck },
    { href: '/trade-finance', label: t('nav.tradeFinance'), icon: Landmark },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm" data-testid="navbar">
      <div className="container-main">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img 
              src={ATFF_LOGO} 
              alt="ATFF - Africa Trading Free Flow Portal" 
              className="h-20 md:h-24 lg:h-28 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                  isActive(link.href)
                    ? 'text-continental bg-continental/5'
                    : 'text-gray-600 hover:text-continental hover:bg-gray-50'
                }`}
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-2">
            {/* Language Switcher */}
            <LanguageSwitcher variant="compact" />

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                    <div className="w-8 h-8 bg-continental/10 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-continental" />
                    </div>
                    <span className="hidden md:inline font-medium text-charcoal">{user?.name?.split(' ')[0]}</span>
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2 border-b">
                    <p className="font-medium text-charcoal">{user?.name}</p>
                    <p className="text-sm text-gray-500">{user?.email}</p>
                  </div>
                  <DropdownMenuItem onClick={() => {
                    // Navigate to role-specific dashboard
                    const role = user?.user_type || user?.role;
                    if (role === 'buyer') navigate('/buyer/dashboard');
                    else if (role === 'logistics') navigate('/logistics/dashboard');
                    else if (role === 'finance') navigate('/finance/dashboard');
                    else if (role === 'government') navigate('/government/dashboard');
                    else if (role === 'admin') navigate('/admin/dashboard');
                    else navigate('/dashboard');
                  }} data-testid="menu-dashboard">
                    <LayoutDashboard className="w-4 h-4 me-2" />
                    {t('nav.dashboard')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/documents')} data-testid="menu-documents">
                    <FileText className="w-4 h-4 me-2" />
                    {t('nav.documents')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                    <LogOut className="w-4 h-4 me-2" />
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/login')}
                  className="hidden sm:inline-flex"
                  data-testid="login-btn"
                >
                  {t('nav.signIn')}
                </Button>
                <Button 
                  onClick={() => navigate('/register')}
                  className="btn-primary"
                  data-testid="register-btn"
                >
                  {t('nav.register')}
                </Button>
              </div>
            )}

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-btn"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden py-4 border-t animate-fade-in">
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setIsOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${
                    isActive(link.href)
                      ? 'text-continental bg-continental/5'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(' ', '-')}`}
                >
                  <link.icon className="w-5 h-5" />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
